import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Users, MessageCircle } from 'lucide-react';
import { followService } from '../services/followService';
import { useAuth } from '../context/AuthContext';
import { config } from '../config/config';
import { useNavigate } from 'react-router-dom';

// --- DARK THEME STILIZACIJA ---
const SupportersContainer = styled.div`min-height: 100vh; background: #000000; padding: 40px 20px;`;
const SupportersHeader = styled.div`max-width: 1200px; margin: 0 auto; text-align: center; margin-bottom: 40px;`;
const SupportersTitle = styled.h1`font-family: var(--font-heading); font-size: 3rem; font-weight: 700; color: #ffffff; margin-bottom: 16px;`;
const SupportersSubtitle = styled.p`font-family: var(--font-body); font-size: 1.2rem; color: #aaaaaa; margin-bottom: 32px;`;
const StatsOverview = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 24px; max-width: 800px; margin: 0 auto 40px;`;
const StatCard = styled.div`background: #111111; border: 1px solid #333333; border-radius: 16px; padding: 24px; text-align: center; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5); .stat-number { font-family: var(--font-heading); font-size: 2.5rem; font-weight: 700; color: #e0c16f; margin-bottom: 8px; } .stat-label { font-family: var(--font-body); font-size: 1rem; color: #aaaaaa; text-transform: uppercase; letter-spacing: 0.5px; }`;
const SupportersList = styled.div`max-width: 1200px; margin: 0 auto;`;
const SupporterCard = styled(motion.div)`background: #111111; border: 1px solid #333333; border-radius: 16px; padding: 24px; margin-bottom: 16px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5); display: grid; grid-template-columns: auto 1fr auto; gap: 20px; align-items: center; transition: border-color 0.2s; &:hover { border-color: #555; } @media(max-width:650px){grid-template-columns:auto 1fr;padding:18px;}`;
const SupporterAvatar = styled.div`width: 60px; height: 60px; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #000000; font-weight: 600; font-size: 1.5rem;`;
const SupporterInfo = styled.div`h3 { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 600; color: #ffffff; margin: 0 0 8px 0; } p { font-family: var(--font-body); color: #888888; margin: 0 0 4px 0; font-size: 0.9rem; }`;
const SupporterActions = styled.div`display: flex; gap: 12px; @media(max-width:650px){grid-column:1/-1;} button { padding: 8px 16px; border: none; border-radius: 8px; font-family: var(--font-heading); font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content:center; gap: 6px; @media(max-width:650px){flex:1;} &.primary { background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000000; &:hover { transform: translateY(-2px); box-shadow: 0 4px 15px rgba(224, 193, 111, 0.3); } } &.secondary { background: transparent; color: #e0c16f; border: 1px solid #e0c16f; &:hover { background: rgba(224, 193, 111, 0.1); } } }`;
const EmptyState = styled.div`text-align: center; padding: 80px 20px; svg { width: 80px; height: 80px; color: #333333; margin-bottom: 24px; } h3 { font-family: var(--font-heading); font-size: 1.8rem; font-weight: 600; color: #ffffff; margin-bottom: 16px; } p { font-family: var(--font-body); color: #888888; font-size: 1.1rem; margin-bottom: 24px; }`;

const CreatorSupporters = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchCreatorAndSupporters();
    }
  }, [user]);

  const fetchCreatorAndSupporters = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // 1. OBAVEZNO PRVO NALAZIMO PRAVI CREATOR ID UMESTO USER.ID!
      const statusRes = await fetch(`${config.API_BASE_URL}/creator-content/creator-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!statusRes.ok) throw new Error('Failed to fetch status');
      const statusData = await statusRes.json();
      
      if (statusData.isCreator) {
        // 2. KORISTIMO CREATOR ID ZA DOHVATANJE (Rešava 404 grešku)
        const data = await followService.getFollowersList(statusData.creatorId);
        
        const mappedSupporters = data.followers ? data.followers.map(sup => ({
          id: sup.user_id,
          username: sup.username,
          displayName: sup.username, 
          avatar: sup.username.charAt(0).toUpperCase(),
          joinedDate: sup.followed_at,
          totalSupport: 0,
          lastSupport: sup.followed_at
        })) : [];
        
        setSupporters(mappedSupporters);
      }
    } catch (error) {
      console.error('Failed to load real supporters:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{padding: '100px', textAlign:'center', color: '#fff'}}>Loading your supporters...</div>;

  return (
    <SupportersContainer>
      <SupportersHeader>
        <SupportersTitle>Your Supporters</SupportersTitle>
        <SupportersSubtitle>Connect with the amazing people who support your work</SupportersSubtitle>
        
        <StatsOverview>
          <StatCard>
            <div className="stat-number">{supporters.length}</div>
            <div className="stat-label">Total Followers</div>
          </StatCard>
          <StatCard>
            <div className="stat-number">$0.00</div>
            <div className="stat-label">Total Earnings</div>
          </StatCard>
        </StatsOverview>
      </SupportersHeader>

      <SupportersList>
        {supporters.length > 0 ? (
          supporters.map((supporter, index) => (
            <SupporterCard key={supporter.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
              <SupporterAvatar>{supporter.avatar}</SupporterAvatar>
              <SupporterInfo>
                <h3>{supporter.displayName}</h3>
                <p>Joined {new Date(supporter.joinedDate).toLocaleDateString()} • Total support: ${(supporter.totalSupport || 0).toFixed(2)}</p>
              </SupporterInfo>
              <SupporterActions>
                <button className="primary" onClick={() => navigate(`/messages/${supporter.id}`, {state:{username:supporter.displayName}})}><MessageCircle size={16} /> Message</button>
              </SupporterActions>
            </SupporterCard>
          ))
        ) : (
          <EmptyState>
            <Users />
            <h3>No supporters yet</h3>
            <p>Share your page with your audience to start building your supporter community.</p>
          </EmptyState>
        )}
      </SupportersList>
    </SupportersContainer>
  );
};

export default CreatorSupporters;
